import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { BottomDock } from '@/shared/ui/BottomDock';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { MonoAmount } from '@/shared/ui/MonoAmount';
import { subscriptionApi } from '@/entities/subscription/api';
import type { Subscription } from '@/entities/subscription/types';
import { amountText, toNumber } from '@/shared/lib/money';
import { useT } from '@/shared/lib/i18n';

function ArchivedSubscriptionCard({ sub }: { sub: Subscription }) {
  const navigate = useNavigate();
  const t = useT();
  const balance = toNumber(sub.balance) ?? 0;
  const letter = sub.name.trim()[0]?.toUpperCase() ?? '?';

  return (
    <button
      className="text-left rounded-[18px] p-4 bg-[rgba(240,250,242,0.34)] border border-white/55 opacity-95 active:scale-[0.985] transition-transform"
      onClick={() => navigate(`/subscriptions/${sub.id}`)}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <SubscriptionIcon letter={letter} size="sm" />
          <b className="text-[16px] truncate">{sub.name}</b>
        </div>
        <StatusBadge
          variant="draft"
          label={sub.type === 'collection' ? t('home.collectionBadge') : t('subscription.status.closed')}
        />
      </div>
      <p className="text-[13px] text-text-muted mt-2">
        {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : t('common.dash')}
      </p>
      <div className="flex justify-between mt-2 text-[14px]">
        <span>{t('archive.finalBalance')}</span>
        <MonoAmount
          value={amountText(balance, sub.currency)}
          variant={balance > 0 ? 'debt' : balance < 0 ? 'credit' : 'paid'}
          size="sm"
          className="font-semibold"
        />
      </div>
    </button>
  );
}

export function ArchivePage() {
  const t = useT();
  const queries = useQueries({
    queries: [
      { queryKey: ['subscriptions', 'holder'], queryFn: () => subscriptionApi.list('holder') },
      { queryKey: ['subscriptions', 'participant'], queryFn: () => subscriptionApi.list('participant') },
    ],
  });

  const archived = useMemo(() => {
    const map = new Map<string, Subscription>();
    queries.forEach((query) => {
      query.data?.items
        .filter((sub) => sub.status === 'closed' || sub.status === 'archived')
        .forEach((sub) => map.set(sub.id, sub));
    });
    return Array.from(map.values());
  }, [queries]);

  return (
    <>
      <Page>
        <PageHeader title={<span className="font-bold text-[19px]">{t('pages.archive')}</span>} />
        <div className="px-[18px] pb-[96px] flex flex-col gap-3">
          <p className="text-[13px] text-text-muted">{t('archive.hint')}</p>
          {archived.map((sub) => <ArchivedSubscriptionCard key={sub.id} sub={sub} />)}
          {archived.length === 0 && (
            <div className="rounded-[18px] glass-content p-6 text-center text-[14px] text-text-secondary">
              {t('archive.empty')}
            </div>
          )}
        </div>
      </Page>
      <BottomDock />
    </>
  );
}
